import {
  Component,
  OnInit,
  OnDestroy,
  ViewEncapsulation,
  computed,
  inject,
} from '@angular/core';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import { TranslocoDirective, TranslocoPipe } from '@jsverse/transloco';
import { Subject, takeUntil } from 'rxjs';
import { MatMenuModule } from '@angular/material/menu';
import { MatIconModule } from '@angular/material/icon';

import { LearningPlanDataService } from '../../../domains/academy/learning-plan/services/learning-plan-data.service';
import { EnrollmentNotificationService } from '../../../core/services/enrollment-notification.service';
import { LearningPlanBuilderDataService } from '../../../domains/academy/learning-plan/services/learning-plan-builder-data.service';

import { LearningPlanEnrolledStudentTableComponent } from '../../../domains/academy/learning-plan/components/learning-plan-enrolled-student-table/learning-plan-enrolled-student-table.component';
import { LearningPlanStructureComponent } from '../../../domains/academy/learning-plan/components/learning-plan-structure/learning-plan-structure.component';
import { LearningPlanStatisticsComponent } from '../../../domains/academy/learning-plan/components/learning-plan-statistics/learning-plan-statistics.component';
import { TranslocoWithFallbackPipe } from '../../../core/pipe/transloco-with-fallback.pipe';
import { AnalyticsService } from '../../../core/services/analytics/analytics.service';

const TRAINING_PHASE_TYPE_TO_FREQUENCY_KEY: Partial<Record<string, string>> = {
  ['Monthly']: 'month',
  ['Quarterly']: 'quarter',
  ['Yearly']: 'year',
};

@Component({
  selector: 'app-learning-plan-detail',
  imports: [
    TranslocoDirective,
    RouterOutlet,
    MatMenuModule,
    MatIconModule,
    LearningPlanEnrolledStudentTableComponent,
    LearningPlanStructureComponent,
    LearningPlanStatisticsComponent,
    TranslocoWithFallbackPipe,
    TranslocoPipe,
  ],
  templateUrl: './learning-plan-detail.component.html',
  styleUrl: './learning-plan-detail.component.css',
  encapsulation: ViewEncapsulation.None,
})
export class LearningPlanDetailComponent implements OnInit, OnDestroy {
  private readonly analyticsService = inject(AnalyticsService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly learningPlanDataService = inject(LearningPlanDataService);
  private readonly enrollmentNotificationService = inject(
    EnrollmentNotificationService,
  );
  private readonly learningPlanBuilderDataService = inject(
    LearningPlanBuilderDataService,
  );

  private readonly destroy$: Subject<void> = new Subject();

  readonly details = this.learningPlanDataService.details;
  readonly canEdit = this.learningPlanDataService.canEdit;
  readonly canEnroll = this.learningPlanDataService.canEnroll;
  readonly isLooping = this.learningPlanDataService.isLooping;
  readonly hasDeadline = this.learningPlanDataService.hasDeadline;
  readonly isDeadlinePassed = this.learningPlanDataService.isDeadlinePassed;
  readonly tableStudents = this.learningPlanDataService.tableStudents;
  readonly periodPickerOptions =
    this.learningPlanDataService.periodPickerOptions;
  readonly periodPickerControl =
    this.learningPlanDataService.periodPickerControl;
  readonly selectedPeriodPickerControlOption =
    this.learningPlanDataService.selectedPeriodPickerControlOption;

  readonly phaseDuration = computed(
    () =>
      TRAINING_PHASE_TYPE_TO_FREQUENCY_KEY[
        this.details()?.trainingPhaseType ?? ''
      ] ?? 'year',
  );
  readonly deadline = computed(() =>
    this.hasDeadline() ? this.details()?.orderedPhases[0]?.endsAt : undefined,
  );

  ngOnInit(): void {
    this.observeLearningPlanInstanceIdInUrlAndFetch();
    this.enrollmentNotificationService
      .getEnrollmentChanged()
      .pipe(takeUntil(this.destroy$))
      .subscribe((changed) => {
        if (changed && this.learningPlanDataService.learningPlanInstanceId()) {
          this.learningPlanDataService.getLearningPlanEnrollments();
        }
      });
  }

  private observeLearningPlanInstanceIdInUrlAndFetch(): void {
    this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      const id = params.get('id');
      if (!id) {
        return;
      }

      this.learningPlanDataService.reset();
      this.learningPlanDataService.learningPlanInstanceId.set(id);

      this.learningPlanDataService.getLearningPlanDetails(id);
    });
  }

  onEnrollButtonClick(): void {
    void this.router.navigate(['dialog'], {
      relativeTo: this.route,
      queryParams: { step: 'enroll' },
    });
  }

  onEditButtonClick(): void {
    const details = this.details();
    if (!details) {
      return;
    }

    this.learningPlanBuilderDataService.setEditedLearningPlan(details);
    void this.router.navigate(['edit'], { relativeTo: this.route });
  }

  onDeleteButtonClick(): void {
    const trainingName = this.learningPlanDataService.learningPlanName();
    const details = this.learningPlanDataService.details();
    const numberOfEnrolledStudents = details?.numberOfEnrolledStudents ?? 0;
    const numberOfCompletedStudents = details?.numberOfCompletedStudents ?? 0;
    const completionRate =
      numberOfEnrolledStudents === 0
        ? 0
        : Math.round(
            (numberOfCompletedStudents / numberOfEnrolledStudents) * 100,
          );
    this.learningPlanDataService.deleteLearningPlan(() => {
      void this.router.navigate(['/portal/dashboard']);
      this.analyticsService.track('training_deleted', {
        trainingName: trainingName ?? '',
        participantCount: numberOfEnrolledStudents,
        completionRate: completionRate,
      });
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
